import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Eye, Check } from "lucide-react";

/**
 * SEARCH simulation. Used by Information Search / Selection / Interpretation.
 * Events: query_entered, query_refined, result_clicked, source_inspected,
 * unreliable_source_selected, misinterpretation_selected, final_decision.
 */
export default function SearchSim({ scenario, recorder, onFinalize, busy }) {
  const queries = scenario.queries || {};
  const goodKw = (scenario.good_query_keywords || []).map((k) => k.toLowerCase());
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [phase, setPhase] = useState("vague"); // vague|refined
  const [inspected, setInspected] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [submitCount, setSubmitCount] = useState(0);

  const submit = () => {
    if (!q.trim()) return;
    const lower = q.toLowerCase();
    const isStrong = goodKw.length === 0 || goodKw.every((k) => lower.includes(k));
    const nextPhase = isStrong && submitCount > 0 ? "refined" : phase;
    if (submitCount === 0) {
      recorder.log("query_entered", q);
    } else {
      recorder.log("query_refined", q);
    }
    const set = isStrong && (queries.refined || queries.vague) ? (queries.refined || queries.vague) : (queries.vague || []);
    setResults(set);
    setPhase(isStrong && submitCount > 0 ? "refined" : "vague");
    setSubmitCount((c) => c + 1);
  };

  const inspect = (r) => {
    setInspected((s) => new Set([...s, r.id]));
    recorder.log("source_inspected", r.id);
    recorder.log("result_clicked", r.id);
  };

  const choose = (r) => {
    setSelected(r);
    if (!r.credible) recorder.log("unreliable_source_selected", r.id);
  };

  const finalize = () => {
    if (!selected) {
      onFinalize("no_selection", {});
      return;
    }
    onFinalize(`select:${selected.id}`, { selectedId: selected.id });
  };

  return (
    <div className="p-5 grid gap-5" data-testid="search-sim">
      <div className="paper border p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Search size={14} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a search query"
            className="flex-1 rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)] h-10"
            data-testid="search-input"
          />
          <Button onClick={submit} className="btn-ink rounded-none h-10" data-testid="btn-search-submit">Search</Button>
        </div>
        <div className="label-mono mt-2">Phase: {phase} · queries submitted: {submitCount}</div>
      </div>

      <div className="grid gap-2">
        {results.length === 0 && <div className="text-sm text-[var(--iddl-muted)]">Run a query to see results.</div>}
        {results.map((r) => (
          <div key={r.id} className={`paper border p-3 ${selected?.id === r.id ? "ring-1 ring-[var(--iddl-ink)]" : ""}`} data-testid={`result-${r.id}`}>
            <div className="font-display font-semibold">{r.title}</div>
            <div className="font-mono-disp text-xs text-[var(--iddl-muted)]">{r.url}</div>
            <p className="text-sm mt-1">{r.snippet}</p>
            <div className="mt-2 flex gap-2 items-center">
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => inspect(r)} data-testid={`btn-inspect-${r.id}`}>
                <Eye size={12} className="mr-1" /> {inspected.has(r.id) ? "Inspected" : "Inspect"}
              </Button>
              <Button size="sm" className={selected?.id === r.id ? "btn-ink rounded-none" : "btn-ghost rounded-none"} onClick={() => choose(r)} data-testid={`btn-select-${r.id}`}>
                <Check size={12} className="mr-1" /> {selected?.id === r.id ? "Selected" : "Select"}
              </Button>
              {r.credible && <span className="label-mono tint-ds px-1.5 py-0.5 border ml-auto">CREDIBLE</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="pt-4 border-t border-[var(--iddl-rule)] flex justify-end">
        <Button onClick={finalize} disabled={busy} className="btn-ink rounded-none" data-testid="btn-finalize-search">Finalise — submit selection</Button>
      </div>
    </div>
  );
}
